import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import type { ActionAvailability } from "@amber/contracts";
import {
  Button,
  Callout,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  FieldLabel,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
  RadioCards,
  ReasonDialog,
  ReasonField,
  TextArea,
  formatDateTime,
  isApiError,
  useReason,
  useToast,
  errorText,
  useCommandMutation,
} from "@amber/ui";
import type { GapRow } from "../../contracts/index";
import { backfillPreviewQuery, gapCommands, AFTER_PLAN_CHANGE, ON_PLAN_CONFLICT } from "../queries";

type GapDialog = "next" | "backfill" | "cancel" | "resolve";

export interface GapActionAvailability {
  readonly gap_decision: ActionAvailability;
  readonly cancel_backfill: ActionAvailability;
}

export function gapInterval(gap: Pick<GapRow, "from" | "to">): string {
  if (!gap.from && !gap.to) return "One item, not an interval";
  return `${gap.from ? formatDateTime(gap.from) : "—"} – ${gap.to ? formatDateTime(gap.to) : "now"}`;
}

const interval = gapInterval;

function MenuEntry({
  label,
  hint,
  disabled,
  onSelect,
}: {
  label: string;
  hint: string;
  disabled: string | null;
  onSelect: () => void;
}) {
  return (
    <MenuItem disabled={disabled !== null} onSelect={onSelect} className="flex-col items-start gap-0">
      <span>{label}</span>
      <small className="text-sm text-muted">{disabled ?? hint}</small>
    </MenuItem>
  );
}

function cancelBackfillDisabled(actions: GapActionAvailability, running: boolean): string | null {
  if (!actions.cancel_backfill.enabled) return actions.cancel_backfill.reason;
  return running ? null : "No backfill is queued or running";
}

export function GapMenu({
  gap,
  actions,
  compact = false,
}: {
  gap: GapRow;
  actions: GapActionAvailability;
  compact?: boolean;
}) {
  const [dialog, setDialog] = useState<GapDialog | null>(null);
  if (gap.resolved) return null;
  const decide = actions.gap_decision.enabled ? null : actions.gap_decision.reason;
  const running = gap.recovery !== null;
  const close = () => {
    setDialog(null);
  };
  return (
    <>
      <MenuRoot>
        <MenuTrigger asChild>
          {compact ? (
            <Button size="sm" variant="ghost" aria-label={`Actions for the gap ${interval(gap)}`}>
              <MoreHorizontal size={14} aria-hidden="true" />
            </Button>
          ) : (
            <Button size="sm">Gap actions</Button>
          )}
        </MenuTrigger>
        <MenuContent className="w-[300px]">
          <MenuEntry
            label="Set next step…"
            hint="Automatic retry, wait for the source, or accept as permanent"
            disabled={decide}
            onSelect={() => {
              setDialog("next");
            }}
          />
          <MenuEntry
            label="Request backfill…"
            hint="Collect the missing interval behind fresh monitoring"
            disabled={decide ?? (running ? "A backfill is already queued or running" : null)}
            onSelect={() => {
              setDialog("backfill");
            }}
          />
          <MenuEntry
            label="Cancel backfill…"
            hint="What was already read stays collected"
            disabled={cancelBackfillDisabled(actions, running)}
            onSelect={() => {
              setDialog("cancel");
            }}
          />
          <MenuEntry
            label="Resolve…"
            hint="Close it with a note on how you know nothing is missing"
            disabled={decide}
            onSelect={() => {
              setDialog("resolve");
            }}
          />
        </MenuContent>
      </MenuRoot>
      {dialog === "next" ? (
        <NextStepDialog
          gap={gap}
          onClose={close}
          onBackfill={() => {
            setDialog("backfill");
          }}
        />
      ) : null}
      {dialog === "backfill" ? <BackfillDialog gap={gap} onClose={close} /> : null}
      {dialog === "cancel" ? <CancelBackfillDialog gap={gap} onClose={close} /> : null}
      {dialog === "resolve" ? <ResolveDialog gap={gap} onClose={close} /> : null}
    </>
  );
}

type Step = "automatic_retry" | "wait_for_source" | "accept_as_permanent" | "backfill";

const STEP_CONFIRM: Record<Step, string> = {
  automatic_retry: "Set next step",
  wait_for_source: "Set next step",
  accept_as_permanent: "Accept as permanent",
  backfill: "Continue",
};

function NextStepDialog({ gap, onClose, onBackfill }: { gap: GapRow; onClose: () => void; onBackfill: () => void }) {
  const toast = useToast();
  const [step, setStep] = useState<Step>(gap.from_scan ? "automatic_retry" : "wait_for_source");
  const [note, setNote] = useState("");
  const r = useReason();
  const noteMissing = step === "accept_as_permanent" && note.trim().length === 0;
  const mutation = useCommandMutation({
    run: (csrf) =>
      gapCommands.nextStep(
        gap.id,
        { next_step: step === "backfill" ? "wait_for_source" : step, note: note.trim(), reason: r.trimmed },
        csrf,
      ),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
    onSuccess: () => {
      toast.show(step === "accept_as_permanent" ? "Gap accepted as permanent" : "Next step set");
      onClose();
    },
  });
  const serverNote = isApiError(mutation.error) ? mutation.error.fieldErrors()["note"] : undefined;
  const options = [
    ...(gap.from_scan
      ? [
          {
            value: "automatic_retry" as const,
            label: "Automatic retry",
            description:
              "The next complete scan reads the interval again; if scans keep failing it needs someone again",
          },
        ]
      : []),
    { value: "backfill" as const, label: "Backfill", description: "Continue to the backfill preview" },
    {
      value: "wait_for_source" as const,
      label: "Wait for the source",
      description: "Read the interval again once scans of this source succeed",
    },
    {
      value: "accept_as_permanent" as const,
      label: "Accept as permanent",
      description: "Closes the gap, for example for deleted or member-only content; no more attempts",
    },
  ];
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Set next step"
      onSubmit={() => {
        if (step === "backfill") {
          onBackfill();
          return;
        }
        if (r.submit() && !noteMissing) mutation.mutate();
      }}
      footer={
        <DialogFooter
          onClose={onClose}
          confirm={STEP_CONFIRM[step]}
          pending={mutation.isPending}
          failed={mutation.isError}
        />
      }
    >
      <DialogText>
        <b>{gap.source.title}</b> · {interval(gap)} · {gap.reason}
      </DialogText>
      <RadioCards label="Next step" value={step} options={options} onChange={setStep} />
      {step === "accept_as_permanent" ? (
        <div className="mt-3">
          <FieldLabel htmlFor="gap-note" required>
            Why nothing more can be collected
          </FieldLabel>
          <TextArea
            id="gap-note"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
            aria-invalid={(r.submitted && noteMissing) || serverNote ? true : undefined}
          />
          {(r.submitted && noteMissing) || serverNote ? (
            <p className="m-0 mt-1.5 text-md text-attention">
              {serverNote ?? "Say why nothing more can be collected for this interval"}
            </p>
          ) : null}
          {gap.to === null ? (
            <p className="m-0 mt-1.5 text-md text-muted">
              The source is still failing: accepting closes the interval now, and new missing time opens a new gap.
            </p>
          ) : null}
        </div>
      ) : null}
      {step !== "backfill" ? <ReasonField value={r.reason} onChange={r.setReason} error={r.error} /> : null}
      <FailedCallout error={serverNote ? null : mutation.error} />
    </Dialog>
  );
}

function BackfillDialog({ gap, onClose }: { gap: GapRow; onClose: () => void }) {
  const preview = useQuery(backfillPreviewQuery(gap.id));
  const p = preview.data;
  return (
    <ReasonDialog
      title="Request backfill"
      confirm="Request backfill"
      success="Backfill queued"
      onClose={onClose}
      run={(reason, csrf) => gapCommands.backfill(gap.id, { reason }, csrf)}
      invalidate={AFTER_PLAN_CHANGE}
      invalidateOnConflict={ON_PLAN_CONFLICT}
      extra={
        <>
          {p ? (
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-md">
              <dt className="text-muted">Interval</dt>
              <dd className="m-0">
                {p.estimate.from ? formatDateTime(p.estimate.from) : "—"} –{" "}
                {p.estimate.to ? formatDateTime(p.estimate.to) : "now"} · {p.estimate.days} days
              </dd>
              <dt className="text-muted">Expected items</dt>
              <dd className="m-0">about {p.estimate.items}</dd>
              <dt className="text-muted">Estimated cost</dt>
              <dd className="m-0">
                {p.estimate.cost.currency} {p.estimate.cost.amount_decimal}
              </dd>
              <dt className="text-muted">Limits</dt>
              <dd className="m-0">{p.caps}</dd>
            </dl>
          ) : (
            <p className="m-0 text-md text-muted">{preview.isError ? errorText(preview.error) : "Estimating…"}</p>
          )}
          {p?.waits ? (
            <Callout tone="waiting" className="mt-3">
              {p.waits}
            </Callout>
          ) : null}
        </>
      }
    >
      Collects the missing interval of <b>{gap.source.title}</b> as low-priority work behind fresh monitoring. The gap
      closes automatically once the whole interval is read.
    </ReasonDialog>
  );
}

function CancelBackfillDialog({ gap, onClose }: { gap: GapRow; onClose: () => void }) {
  return (
    <ReasonDialog
      title="Cancel backfill"
      confirm="Cancel backfill"
      success="Backfill cancelled"
      onClose={onClose}
      run={(reason, csrf) => gapCommands.cancelBackfill(gap.id, { reason }, csrf)}
      invalidate={AFTER_PLAN_CHANGE}
      invalidateOnConflict={ON_PLAN_CONFLICT}
    >
      Stops the backfill of <b>{gap.source.title}</b> for {interval(gap)}. What was already read stays collected; the
      gap then needs someone and a task is created.
    </ReasonDialog>
  );
}

function ResolveDialog({ gap, onClose }: { gap: GapRow; onClose: () => void }) {
  const toast = useToast();
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const mutation = useCommandMutation({
    run: (csrf) => gapCommands.resolve(gap.id, { note: note.trim() }, csrf),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
    onSuccess: () => {
      toast.show("Gap resolved");
      onClose();
    },
  });
  const missing = note.trim().length === 0;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Resolve gap"
      onSubmit={() => {
        setSubmitted(true);
        if (!missing) mutation.mutate();
      }}
      footer={
        <DialogFooter onClose={onClose} confirm="Resolve" pending={mutation.isPending} failed={mutation.isError} />
      }
    >
      <DialogText>
        Gaps normally close automatically. Resolve <b>{gap.source.title}</b> · {interval(gap)} by hand only if you know
        nothing is missing, or that it was collected another way.
      </DialogText>
      <div className="mt-3">
        <FieldLabel htmlFor="resolve-note" required>
          Note
        </FieldLabel>
        <TextArea
          id="resolve-note"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
          aria-invalid={submitted && missing ? true : undefined}
          placeholder="How you know nothing is missing"
        />
        {submitted && missing ? (
          <p className="m-0 mt-1.5 text-md text-attention">
            Say how you know nothing is missing, or how it was collected
          </p>
        ) : null}
      </div>
      <FailedCallout error={mutation.error} />
    </Dialog>
  );
}
